<template>
  <div
    id="search"
    class="search"
    :style="themedButtons"
    @keydown.esc.exact.prevent="closeModal"
  >
    <div
      ref="modalContainer"
      class="search-container"
    >
      <button
        ref="closeButton"
        class="close-button"
        :aria-label="$t('search.closeSearchDescriptive')"
        @click="closeModal"
      >
        <Icon name="lucide:x" />
        {{ $t('search.closeSearch') }}
      </button>
      <SearchBar
        :class="{ 'feedback-modal-open': isFeedbackModalOpen }"
        :initial-search-term="route.query.q"
        @full-search-initiated="handleFullSearchInitiated"
      />
      <SearchResults
        v-if="showResultsPanel"
        :class="{ 'feedback-modal-open': isFeedbackModalOpen }"
        :is-loading="status === 'pending'"
        :results="searchResults"
        :ai-data="aiData"
        :show-ai-answer="showAiAnswer"
        @result-clicked="handleResultClick"
        @filter-applied="handleFilterApplied"
        @open-feedback-modal="openFeedbackModal"
        @continue-in-chat="handleContinueInChat"
      />
      <FeedbackModal
        v-if="isFeedbackModalOpen"
        @close-feedback-modal="closeFeedbackModal"
        @submit-feedback="submitFeedbackWithComment"
        @close-confirmation-modal="closeConfirmationModal"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import { onClickOutside, useScrollLock } from '@vueuse/core';
import {
  trackSearchResultsView,
  trackSearchClick,
  trackNoResults,
  trackSearchLatencyRecorded,
  trackFeedbackSubmitted,
  trackSearchAbandoned,
} from '~/helpers/searchAnalytics';
import { useStore } from 'vuex';
import SearchBar from './SearchBar.vue';
import SearchResults from './SearchResults.vue';
import { useThemedButtons } from '@/composables/useThemedButtons';
import FeedbackModal from './FeedbackModal.vue';
import { useFeedback } from '@/composables/useFeedback';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { useAiAnswer } from '@/composables/useAiAnswer';
import { useAiChatWidget } from '@/composables/useAiChatWidget';

const { aiData, startPolling: pollAiAnswer, stopPolling: stopAiAnswerPolling } = useAiAnswer();
const { $t } = useNuxtApp();
const showAiAnswer = ref(false);

const { themedButtons } = useThemedButtons();
const { submitFeedback } = useFeedback();
// Modal scroll lock and close modal on outside click
useScrollLock(document.body, true);
const modalContainer = useTemplateRef('modalContainer');

const searchStartTime = ref(0);
const hasInteractedWithResults = ref(false);

const { hasFocus, activate } = useFocusTrap(modalContainer, {
  immediate: true,
  returnFocusOnDeactivate: false,
  clickOutsideDeactivates: true,
});
onMounted(() => {
  // Listen for focus returning to modal, if it was deactivated by clicking outside re-activate the focus trap
  modalContainer?.value.addEventListener('focusin', () => {
    if (!hasFocus.value) {
      activate();
    }
  });
});

function closeModal() {
  if (searchStartTime.value > 0 && !hasInteractedWithResults.value && route.query.q) {
    const timeOnResultsPage = performance.now() - searchStartTime.value;
    trackSearchAbandoned(route.query.q, timeOnResultsPage);
  }
  // eslint-disable-next-line no-unused-vars
  const { secondary_organizations, query_indices, q, start_date, end_date, ...currentQuery } = route.query;
  return navigateTo({ query: currentQuery }, { replace: true });
}

// Allow clicking on AI chat and alwaysOn
const ignoreElements = ['.ai-chat-widget-container', '#alwayson_chatbot'];
onClickOutside(modalContainer, closeModal, {
  ignore: ignoreElements,
});

const route = useRoute();
const store = useStore();

const { data, status, error, execute } = await useAsyncData(
  `is-${route.query.q}`,
  () => {
    const { query } = route;
    const { q, secondary_organizations, query_indices, start_date, end_date } = query;

    const toArray = (value) => {
      if (!value) {
        return [];
      }
      return Array.isArray(value) ? value : [value];
    };

    return store.dispatch('getSearchResults', {
      term: q,
      orgIds: toArray(secondary_organizations),
      selectedFilters: toArray(query_indices),
      startDate: start_date,
      endDate: end_date,
      translate: true,
    });
  },
  {
    immediate: false,
  },
);

const showResultsPanel = ref(false);
const searchResults = ref([]);
const isFeedbackModalOpen = ref(false);

async function updateSearchQuery(searchQuery) {
  try {
    await navigateTo({ query: { ...searchQuery } }, { replace: true });
  } catch {
    console.error(error);
  }
}

async function executeSearch() {
  try {
    const startTime = performance.now();

    await execute();

    if (data.value.data.results.length > 0) {
      searchStartTime.value = performance.now();
      hasInteractedWithResults.value = false;
    }

    const latency = performance.now() - startTime;
    if (route.query.q) {
      trackSearchLatencyRecorded(route.query.q, latency);
    }

    return data;
  } catch {
    console.error(error);
  }
}

async function handleFullSearchInitiated(queryUpdates) {
  const newQuery = { ...route.query };

  // Update query with new search updates (null/undefined = delete parameter)
  Object.entries(queryUpdates).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      delete newQuery[key];
    } else {
      newQuery[key] = value;
    }
  });

  // Apply default org filter if no existing org filters and it's first search
  if (initOrgFilter.value && !newQuery.secondary_organizations) {
    newQuery.secondary_organizations = [initOrgFilter.value];
    initOrgFilter.value = null;
  }

  await updateSearchQuery(newQuery);
  showResultsPanel.value = true;
}

const openFeedbackModal = () => {
  isFeedbackModalOpen.value = true;
  hasInteractedWithResults.value = true;
};

const initOrgFilter = ref(null);
onMounted(() => {
  const hasExistingOrgFilters = route.query.secondary_organizations;
  const currentOrgId = store.state.secondaryOrgId;
  if (!hasExistingOrgFilters && currentOrgId) {
    initOrgFilter.value = currentOrgId;
  }
});
onUnmounted(() => {
  stopAiAnswerPolling();
});

const closeFeedbackModal = () => {
  const feedbackData = store.getters.getFeedbackData;
  const sentiment = store.getters.getFeedbackSentiment;
  submitFeedback(feedbackData, sentiment);
  isFeedbackModalOpen.value = false;
  trackFeedbackSubmitted(sentiment, null, route.query.q);
};
const closeConfirmationModal = () => {
  isFeedbackModalOpen.value = false;
};

const submitFeedbackWithComment = (comment) => {
  const feedbackData = store.getters.getFeedbackData;
  const sentiment = store.getters.getFeedbackSentiment;
  submitFeedback(feedbackData, sentiment, comment);
  trackFeedbackSubmitted(sentiment, comment, route.query.q);
};

function formatActiveFiltersForAnalytics(routeQuery) {
  const activeFilters = [];

  if (routeQuery.secondary_organizations && routeQuery.secondary_organizations.length > 0) {
    const values = Array.isArray(routeQuery.secondary_organizations)
      ? routeQuery.secondary_organizations
      : [routeQuery.secondary_organizations];

    activeFilters.push({
      filter_name: 'schools',
      filter_values: values.join(','),
    });
  }

  if (routeQuery.query_indices && routeQuery.query_indices.length > 0) {
    const values = Array.isArray(routeQuery.query_indices) ? routeQuery.query_indices : [routeQuery.query_indices];

    activeFilters.push({
      filter_name: 'type',
      filter_values: values.join(','),
    });
  }

  if (routeQuery.start_date && routeQuery.end_date) {
    const today = new Date();
    const fullDateFormatter = new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    const formattedDateToday = fullDateFormatter.format(today);
    const formattedDateLast7Days = fullDateFormatter.format(today - 7 * 24 * 60 * 60 * 1000);
    const formattedDateLast30Days = fullDateFormatter.format(today - 30 * 24 * 60 * 60 * 1000);
    const currentYear = new Date().getFullYear();
    const lastYear = currentYear - 1;

    let dateFilterValue = 'custom';

    if (routeQuery.start_date === formattedDateToday && routeQuery.end_date === formattedDateToday) {
      dateFilterValue = 'today';
    } else if (routeQuery.start_date === formattedDateLast7Days && routeQuery.end_date === formattedDateToday) {
      dateFilterValue = 'last7Days';
    } else if (routeQuery.start_date === formattedDateLast30Days && routeQuery.end_date === formattedDateToday) {
      dateFilterValue = 'last30Days';
    } else if (routeQuery.start_date === `${currentYear}-01-01` && routeQuery.end_date === `${currentYear}-12-31`) {
      dateFilterValue = 'thisYear';
    } else if (routeQuery.start_date === `${lastYear}-01-01` && routeQuery.end_date === `${lastYear}-12-31`) {
      dateFilterValue = 'lastYear';
    }

    activeFilters.push({
      filter_name: 'date',
      filter_value: dateFilterValue,
    });
  }

  return activeFilters;
}

function handleResultClick(event) {
  const { position, url } = event;
  const searchTerm = route.query.q || '';
  if (searchTerm) {
    trackSearchClick(searchTerm, position, url);
  }
}

// Initial search
if (route.query.q) {
  executeSearch();
}

const { openAiChat, sendMessage, aiSearchEnabled } = useAiChatWidget();
async function handleContinueInChat() {
  const searchTerm = route.query.q || '';
  await closeModal();
  openAiChat();
  await nextTick();
  sendMessage(searchTerm, {
    icon: 'lucide:search',
    text: $t('search.aiAnswer.searchInquiry'),
  });
}

watch(
  () => route.query,
  async () => {
    const q = route.query?.q || '';
    const hasSearchTerm = q.trim().length > 0;

    const filters = {
      secondary_organizations: route.query?.secondary_organizations,
      query_indices: route.query?.query_indices,
      start_date: route.query?.start_date,
      end_date: route.query?.end_date,
    };

    const hasFilters = Object.values(filters).some((val) => (Array.isArray(val) ? val.length > 0 : !!val));

    if (hasFilters || hasSearchTerm) {
      showAiAnswer.value = false;
      stopAiAnswerPolling();
      const startTime = performance.now();
      const searchReturns = await executeSearch();
      const resultTimeMs = Math.round(performance.now() - startTime);
      const { chat_id, session_id } = searchReturns.value?.data ?? {};
      if (chat_id && session_id && aiSearchEnabled.value) {
        showAiAnswer.value = true;
        pollAiAnswer(chat_id, session_id);
      }
      const results = searchReturns.value?.data?.results ?? [];
      searchResults.value = results;

      if (hasSearchTerm) {
        if (results.length > 0) {
          trackSearchResultsView(q, results.length, resultTimeMs);
        } else {
          const activeFilters = formatActiveFiltersForAnalytics(route.query);
          trackNoResults(q, q.length, activeFilters);
        }
      }
    }
  },
  { deep: true, immediate: true },
);
</script>

<style lang="scss" scoped>
@use '../global/scss/mixins/_breakpoints.scss' as bp;

#search {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  background: rgba(34, 34, 34, 0.8);
  z-index: 10;

  /* Centralized font-family for all search components - using :deep() to cascade to child components */
  :deep(*) {
    font-family: var(--font-family);
  }

  .search-container {
    width: 50vw;
    height: 85vh;
    background: transparent;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 8px;
    position: relative;

    .feedback-modal-open {
      pointer-events: none;
    }
  }
}

.close-button {
  opacity: 1;
  margin-left: auto;
  display: flex;
  gap: 4px;
  align-items: center;
  background: none;
  padding: 5px;
  border: none;
  color: var(--primary-white);

  font-size: 14px;
  font-weight: 500;
  line-height: 19px;
  letter-spacing: 0.6px;

  &:hover {
    cursor: pointer;
  }
  &:focus-visible {
    outline-offset: 2px;
  }
}

@include bp.screen('>=lg') {
  #search .search-container {
    position: absolute;
    left: 50%;
    top: 0;
    transform: translate(-50%, 5%);
    max-width: 1200px;
    padding: 16px;
  }
}

@include bp.screen('<=md') {
  #search {
    --search-bar-height: 82px;
    .search-container {
      position: absolute;
      top: 0;
      padding: 16px 24px;
      width: calc(100vw - 48px);
      height: calc(100vh - 48px);
    }
  }
  :deep(.backdrop) {
    .modal {
      width: calc(90vw - 30px);
      height: calc(50vh);
      &.confirmation {
        width: calc(90vw - 30px);
      }
    }
  }
}

@include bp.screen('xs') {
  #search .search-container {
    --search-bar-height: 74px;
    width: 100%;
    height: 100%;
    gap: 0;
    padding: 0;
    .close-button {
      margin: 6px 6px 7px 0;
      margin-left: auto;
    }
  }
  :deep(.backdrop) {
    .modal {
      width: calc(100vw - 30px);
      height: calc(60vh);
      &.confirmation {
        width: calc(100vw - 30px);
        height: auto;
      }
    }
  }
}
</style>
